<template>
  <div class="two-factor-auth">
    <!-- loading -->
    <div v-if="view === 'loading'" class="text-center py-5">
      <b-spinner variant="primary" label="กำลังโหลด"></b-spinner>
    </div>

    <!-- disabled: not enrolled -->
    <div v-else-if="view === 'disabled'">
      <p class="mb-3">
        เพิ่มการยืนยันตัวตนอีกชั้นด้วยแอปสร้างรหัส เช่น Google Authenticator หรือ
        Microsoft Authenticator
      </p>

      <div v-if="!showSetupPassword">
        <button
          type="button"
          class="btn btn-primary"
          data-testid="mfa-enable"
          @click="startEnable"
        >
          เปิดใช้งาน
        </button>
      </div>

      <b-form v-else @submit.prevent="submitSetup">
        <div class="form-group">
          <label for="mfaSetupPassword">ยืนยันรหัสผ่านปัจจุบัน</label>
          <b-form-input
            id="mfaSetupPassword"
            type="password"
            autocomplete="current-password"
            data-testid="mfa-setup-password"
            v-model="setupPassword"
          ></b-form-input>
          <div class="description error" v-if="setupError">{{ setupError }}</div>
        </div>
        <button type="submit" class="btn btn-primary mr-2">ต่อไป</button>
        <button type="button" class="btn btn-light" @click="cancelSetup">
          ยกเลิก
        </button>
      </b-form>
    </div>

    <!-- enrolling: scan QR + confirm code -->
    <div v-else-if="view === 'enrolling'">
      <p class="mb-3">
        สแกน QR โค้ดนี้ด้วยแอปยืนยันตัวตน จากนั้นกรอกรหัส 6 หลักเพื่อยืนยัน
      </p>
      <div class="text-center mb-3">
        <img
          :src="setup.qrDataUrl"
          alt="QR code"
          data-testid="mfa-qr"
          class="mfa-qr-img"
        />
      </div>

      <div class="form-group">
        <label for="mfaSecret">หรือกรอกรหัสนี้ด้วยตนเอง</label>
        <div class="input-group">
          <b-form-input
            id="mfaSecret"
            class="mfa-secret"
            readonly
            :value="setup.secretBase32"
          ></b-form-input>
          <div class="input-group-append">
            <button
              type="button"
              class="btn btn-outline-secondary"
              @click="copySecret"
            >
              คัดลอก
            </button>
          </div>
        </div>
      </div>

      <b-form @submit.prevent="submitActivate">
        <div class="form-group">
          <label for="mfaActivateCode">กรอกรหัส 6 หลัก</label>
          <b-form-input
            id="mfaActivateCode"
            inputmode="numeric"
            autocomplete="one-time-code"
            maxlength="6"
            data-testid="mfa-activate-code"
            :value="activateCode"
            @input="activateCode = onlyDigits($event)"
          ></b-form-input>
          <div class="description error" v-if="activateError">
            {{ activateError }}
          </div>
        </div>
        <button
          type="submit"
          class="btn btn-primary mr-2"
          data-testid="mfa-activate-submit"
        >
          ยืนยัน
        </button>
        <button type="button" class="btn btn-light" @click="cancelSetup">
          ยกเลิก
        </button>
      </b-form>
    </div>

    <!-- codes: recovery codes shown once -->
    <div v-else-if="view === 'codes'">
      <b-alert show variant="warning" class="mb-3">
        เก็บรหัสเหล่านี้ไว้ในที่ปลอดภัย จะแสดงเพียงครั้งเดียว
      </b-alert>

      <div class="recovery-codes mb-3" data-testid="mfa-recovery-codes">
        <div v-for="c in recoveryCodes" :key="c" class="recovery-code">
          {{ c }}
        </div>
      </div>

      <div class="mb-3">
        <button
          type="button"
          class="btn btn-outline-secondary mr-2"
          @click="copyAllCodes"
        >
          คัดลอกทั้งหมด
        </button>
        <button
          type="button"
          class="btn btn-outline-secondary"
          @click="downloadCodes"
        >
          ดาวน์โหลด
        </button>
      </div>

      <button
        type="button"
        class="btn btn-primary"
        data-testid="mfa-codes-done"
        @click="codesDone"
      >
        เสร็จสิ้น
      </button>
    </div>

    <!-- enabled: enrolled -->
    <div v-else-if="view === 'enabled'">
      <b-alert show variant="success" class="mb-3">
        เปิดใช้งานแล้ว
        <span v-if="status && status.activatedDt">
          (เมื่อ {{ formatDate(status.activatedDt) }})
        </span>
      </b-alert>

      <p class="mb-3">
        รหัสสำรองที่เหลือ:
        {{ status ? status.remainingRecoveryCodes : 0 }}
      </p>

      <!-- trusted devices -->
      <div class="mb-4">
        <h6>อุปกรณ์ที่เชื่อถือ</h6>
        <p
          v-if="!status || !status.trustedDevices || !status.trustedDevices.length"
          class="text-muted"
        >
          ไม่มีอุปกรณ์ที่เชื่อถือ
        </p>
        <table v-else class="table table-sm">
          <tbody>
            <tr v-for="d in status.trustedDevices" :key="d.id">
              <td>{{ d.label || d.lastIp || "-" }}</td>
              <td>ใช้ล่าสุด: {{ formatDate(d.lastUsedDt) }}</td>
              <td class="text-right">
                <button
                  type="button"
                  class="btn btn-sm btn-outline-danger"
                  :data-testid="`mfa-revoke-device-${d.id}`"
                  @click="revokeDevice(d.id)"
                >
                  เพิกถอน
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- passkeys -->
      <div class="mb-4">
        <h6>Passkey</h6>
        <p class="mb-2">
          เพิ่ม Passkey เพื่อเข้าสู่ระบบด้วยลายนิ้วมือหรือสแกนหน้า
          โดยไม่ต้องกรอกรหัส
        </p>

        <p
          v-if="!status || !status.passkeys || !status.passkeys.length"
          class="text-muted"
        >
          ยังไม่มี Passkey
        </p>
        <table v-else class="table table-sm">
          <tbody>
            <tr v-for="p in status.passkeys" :key="p.id">
              <td>{{ p.nickname || "อุปกรณ์ที่ไม่ได้ตั้งชื่อ" }}</td>
              <td>ใช้ล่าสุด: {{ formatDate(p.lastUsedDt) }}</td>
              <td class="text-right">
                <button
                  type="button"
                  class="btn btn-sm btn-outline-danger"
                  :data-testid="`passkey-revoke-${p.id}`"
                  @click="revokePasskey(p.id)"
                >
                  เพิกถอน
                </button>
              </td>
            </tr>
          </tbody>
        </table>

        <!-- The button is never rendered on a browser without WebAuthn; the
             muted line takes its place so the absence is explained. -->
        <p v-if="!passkeySupported" class="text-muted mb-0">
          อุปกรณ์นี้ไม่รองรับ Passkey
        </p>
        <b-form
          v-else-if="passkeyPrompt"
          @submit.prevent="submitPasskey"
          class="mb-0"
        >
          <div class="form-group">
            <label for="passkeyPassword">รหัสผ่านปัจจุบัน</label>
            <b-form-input
              id="passkeyPassword"
              type="password"
              autocomplete="current-password"
              data-testid="passkey-password"
              v-model="passkeyPassword"
            ></b-form-input>
          </div>
          <div class="form-group">
            <label for="passkeyNickname">ชื่อเรียกอุปกรณ์ (ไม่บังคับ)</label>
            <b-form-input
              id="passkeyNickname"
              type="text"
              maxlength="60"
              placeholder="เช่น MacBook ที่ทำงาน"
              data-testid="passkey-nickname"
              v-model="passkeyNickname"
            ></b-form-input>
          </div>
          <div class="description error" v-if="passkeyError">
            {{ passkeyError }}
          </div>
          <button
            type="submit"
            class="btn btn-primary mr-2"
            data-testid="passkey-submit"
          >
            ยืนยัน
          </button>
          <button
            type="button"
            class="btn btn-light"
            @click="closePasskeyPrompt"
          >
            ยกเลิก
          </button>
        </b-form>
        <button
          v-else
          type="button"
          class="btn btn-outline-primary"
          data-testid="passkey-add"
          @click="openPasskeyPrompt"
        >
          เพิ่ม Passkey
        </button>
      </div>

      <!-- password + code prompt for disable / regenerate -->
      <b-form v-if="prompt" @submit.prevent="submitPrompt" class="mb-3">
        <div class="form-group">
          <label for="mfaPromptPassword">รหัสผ่านปัจจุบัน</label>
          <b-form-input
            id="mfaPromptPassword"
            type="password"
            autocomplete="current-password"
            data-testid="mfa-prompt-password"
            v-model="promptPassword"
          ></b-form-input>
        </div>
        <div class="form-group">
          <label for="mfaPromptCode">รหัส 6 หลักจากแอป</label>
          <b-form-input
            id="mfaPromptCode"
            inputmode="numeric"
            autocomplete="one-time-code"
            maxlength="6"
            data-testid="mfa-prompt-code"
            :value="promptCode"
            @input="promptCode = onlyDigits($event)"
          ></b-form-input>
        </div>
        <div class="description error" v-if="promptError">{{ promptError }}</div>
        <button
          type="submit"
          class="btn mr-2"
          :class="prompt === 'disable' ? 'btn-danger' : 'btn-primary'"
          data-testid="mfa-prompt-submit"
        >
          {{ prompt === "disable" ? "ยืนยันการปิดใช้งาน" : "ยืนยัน" }}
        </button>
        <button type="button" class="btn btn-light" @click="closePrompt">
          ยกเลิก
        </button>
      </b-form>

      <div v-else>
        <button
          type="button"
          class="btn btn-outline-primary mr-2"
          @click="openPrompt('regenerate')"
        >
          สร้างรหัสสำรองใหม่
        </button>
        <button
          type="button"
          class="btn btn-outline-danger"
          data-testid="mfa-disable"
          @click="openPrompt('disable')"
        >
          ปิดใช้งาน
        </button>
      </div>
    </div>

    <Loading v-if="isLoading"></Loading>
    <Dialog ref="Dialog"></Dialog>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import Loading from "@/components/Loading";
import Dialog from "@/components/modal/Dialog";
import { isPasskeySupported, startRegistration } from "@/utils/webauthn";

// A genuine user cancel takes at least a moment: the prompt has to render
// and the user has to dismiss it. A lost-transient-activation failure (the
// network round trip before the ceremony ate WebKit's user-gesture window)
// comes back almost immediately instead. Below this threshold we treat the
// "cancelled" result as "the prompt never appeared" and say so.
const PASSKEY_LOST_ACTIVATION_THRESHOLD_MS = 400;

export default {
  name: "TwoFactorAuth",
  components: {
    Loading,
    Dialog,
  },
  data() {
    return {
      view: "loading",
      isLoading: false,

      // enrolled status (GET /api/user/mfa)
      status: null,

      // enrolling / setup
      setup: { otpauthUri: "", secretBase32: "", qrDataUrl: "" },
      showSetupPassword: false,
      setupPassword: "",
      setupError: "",

      // activation
      activateCode: "",
      activateError: "",

      // recovery codes (shown once)
      recoveryCodes: [],

      // disable / regenerate prompt: null | "disable" | "regenerate"
      prompt: null,
      promptPassword: "",
      promptCode: "",
      promptError: "",

      // passkey: capability is resolved once on mount, never in the template
      passkeySupported: false,
      passkeyPrompt: false,
      passkeyPassword: "",
      passkeyNickname: "",
      passkeyError: "",
    };
  },
  mounted() {
    this.passkeySupported = isPasskeySupported();
    this.fetchStatus();
  },
  methods: {
    ...mapActions({
      getMfaStatus: "moduleUser/getMfaStatus",
      mfaSetup: "moduleUser/mfaSetup",
      mfaActivate: "moduleUser/mfaActivate",
      mfaDisable: "moduleUser/mfaDisable",
      mfaRegenerate: "moduleUser/mfaRegenerate",
      revokeTrustedDevice: "moduleUser/revokeTrustedDevice",
      passkeyRegisterStart: "moduleUser/passkeyRegisterStart",
      passkeyRegisterFinish: "moduleUser/passkeyRegisterFinish",
      revokePasskeyRequest: "moduleUser/revokePasskey",
    }),

    // Public: called by the parent modal when the tab is (re)opened.
    resetForm() {
      this.fetchStatus();
    },

    async fetchStatus() {
      this.view = "loading";
      try {
        const res = await this.getMfaStatus();
        this.status = res;
        this.view = res && res.enrolled ? "enabled" : "disabled";
      } catch (err) {
        this.view = "disabled";
        this.showDialogToast("danger", "ไม่สามารถโหลดสถานะการยืนยันตัวตนได้");
      } finally {
        this.clearForms();
      }
    },

    // ---- disabled → enrolling ----
    startEnable() {
      this.setupError = "";
      this.setupPassword = "";
      this.showSetupPassword = true;
    },
    cancelSetup() {
      this.showSetupPassword = false;
      this.setupPassword = "";
      this.setupError = "";
      this.activateCode = "";
      this.activateError = "";
      this.setup = { otpauthUri: "", secretBase32: "", qrDataUrl: "" };
      this.view = "disabled";
    },
    async submitSetup() {
      if (!this.setupPassword) {
        this.setupError = "กรุณากรอกรหัสผ่าน";
        return;
      }
      this.setupError = "";
      this.isLoading = true;
      try {
        const res = await this.mfaSetup(this.setupPassword);
        this.setup = {
          otpauthUri: res.otpauthUri,
          secretBase32: res.secretBase32,
          qrDataUrl: res.qrDataUrl,
        };
        this.setupPassword = "";
        this.activateCode = "";
        this.activateError = "";
        this.view = "enrolling";
      } catch (err) {
        const s = err.response && err.response.status;
        if (s === 401) {
          this.setupError = "รหัสผ่านไม่ถูกต้อง";
        } else if (s === 423) {
          this.setupError = "บัญชีถูกล็อกชั่วคราว";
        } else {
          this.setupError = "ไม่สามารถเริ่มการตั้งค่าได้";
        }
      } finally {
        this.isLoading = false;
      }
    },

    // ---- enrolling → codes ----
    async submitActivate() {
      if (!this.activateCode || this.activateCode.length !== 6) {
        this.activateError = "กรุณากรอกรหัส 6 หลัก";
        return;
      }
      this.activateError = "";
      this.isLoading = true;
      try {
        const res = await this.mfaActivate(this.activateCode);
        this.recoveryCodes = res.recoveryCodes || [];
        this.view = "codes";
      } catch (err) {
        const s = err.response && err.response.status;
        if (s === 400 || s === 401) {
          this.activateError = "รหัสไม่ถูกต้อง กรุณาลองใหม่";
        } else if (s === 423) {
          this.activateError = "บัญชีถูกล็อกชั่วคราว";
        } else {
          this.activateError = "ไม่สามารถยืนยันได้ กรุณาลองใหม่";
        }
        this.activateCode = "";
      } finally {
        this.isLoading = false;
      }
    },

    // ---- codes ----
    copyAllCodes() {
      this.copyText(this.recoveryCodes.join("\n"));
    },
    downloadCodes() {
      try {
        const blob = new Blob([this.recoveryCodes.join("\n")], {
          type: "text/plain;charset=utf-8",
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "dentcloud-recovery-codes.txt";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } catch (err) {
        this.showDialogToast("danger", "ไม่สามารถดาวน์โหลดได้");
      }
    },
    async codesDone() {
      this.recoveryCodes = [];
      this.setup = { otpauthUri: "", secretBase32: "", qrDataUrl: "" };
      this.activateCode = "";
      this.activateError = "";
      await this.fetchStatus();
    },

    // ---- enabled: disable / regenerate ----
    openPrompt(type) {
      // only one password form on screen at a time
      this.closePasskeyPrompt();
      this.prompt = type;
      this.promptPassword = "";
      this.promptCode = "";
      this.promptError = "";
    },
    closePrompt() {
      this.prompt = null;
      this.promptPassword = "";
      this.promptCode = "";
      this.promptError = "";
    },
    async submitPrompt() {
      if (!this.promptPassword || !this.promptCode) {
        this.promptError = "กรุณากรอกรหัสผ่านและรหัส 6 หลัก";
        return;
      }
      if (this.promptCode.length !== 6) {
        this.promptError = "กรุณากรอกรหัส 6 หลัก";
        return;
      }
      this.promptError = "";
      this.isLoading = true;
      const payload = {
        password: this.promptPassword,
        code: this.promptCode,
      };
      const isDisable = this.prompt === "disable";
      try {
        if (isDisable) {
          await this.mfaDisable(payload);
          this.closePrompt();
          this.showDialogToast("success", "ปิดใช้งานการยืนยันตัวตนสองชั้นแล้ว");
          await this.fetchStatus();
        } else {
          const res = await this.mfaRegenerate(payload);
          this.recoveryCodes = res.recoveryCodes || [];
          this.closePrompt();
          this.view = "codes";
        }
      } catch (err) {
        const s = err.response && err.response.status;
        if (s === 401) {
          this.promptError = "รหัสผ่านหรือรหัสยืนยันไม่ถูกต้อง";
        } else if (s === 423) {
          this.promptError = "บัญชีถูกล็อกชั่วคราว";
        } else if (
          isDisable &&
          s === 400 &&
          this.isPasskeyBlockingDisable(err)
        ) {
          // TOTP may not be removed while a passkey exists (a passkey can never
          // be the only factor), so say exactly what has to happen first.
          this.promptError =
            "กรุณาลบ Passkey ทั้งหมดก่อนปิดการยืนยันตัวตนด้วยรหัส";
        } else {
          this.promptError = isDisable
            ? "ไม่สามารถปิดใช้งานได้"
            : "ไม่สามารถสร้างรหัสสำรองใหม่ได้";
        }
      } finally {
        this.isLoading = false;
      }
    },

    // ---- trusted devices ----
    async revokeDevice(id) {
      this.isLoading = true;
      try {
        await this.revokeTrustedDevice(id);
        this.showDialogToast("success", "เพิกถอนอุปกรณ์แล้ว");
        await this.fetchStatus();
      } catch (err) {
        this.showDialogToast("danger", "ไม่สามารถเพิกถอนอุปกรณ์ได้");
      } finally {
        this.isLoading = false;
      }
    },

    // ---- passkeys ----
    openPasskeyPrompt() {
      // only one password form on screen at a time
      this.closePrompt();
      this.passkeyPrompt = true;
      this.passkeyPassword = "";
      this.passkeyNickname = "";
      this.passkeyError = "";
    },
    closePasskeyPrompt() {
      this.passkeyPrompt = false;
      this.passkeyPassword = "";
      this.passkeyNickname = "";
      this.passkeyError = "";
    },
    async submitPasskey() {
      if (!this.passkeyPassword) {
        this.passkeyError = "กรุณากรอกรหัสผ่าน";
        return;
      }
      this.passkeyError = "";
      this.isLoading = true;
      // which round trip failed: the 400 meanings differ between the two
      let stage = "start";
      let startedAt;
      try {
        const started = await this.passkeyRegisterStart(this.passkeyPassword);
        // the browser's own passkey prompt (Touch ID / Windows Hello / phone)
        startedAt = Date.now();
        const response = await startRegistration(started.options);
        stage = "finish";
        const nickname = (this.passkeyNickname || "").trim();
        await this.passkeyRegisterFinish({
          response,
          registrationToken: started.registrationToken,
          nickname: nickname || undefined,
        });
        this.closePasskeyPrompt();
        this.showDialogToast("success", "เพิ่ม Passkey แล้ว");
        await this.fetchStatus();
      } catch (err) {
        if (err && err.cancelled) {
          if (Date.now() - startedAt < PASSKEY_LOST_ACTIVATION_THRESHOLD_MS) {
            // Came back too fast to be a real cancel: the browser most likely
            // never showed the prompt at all (WebKit lost transient user
            // activation during the network round trip beforehand). Say so
            // instead of silently doing nothing, via the existing
            // prompt-error mechanism.
            this.passkeyError =
              "ไม่สามารถเปิดหน้าต่าง Passkey ได้ กรุณาลองใหม่อีกครั้ง";
            return;
          }
          // the user dismissed the browser prompt -- not an error, say nothing
          this.closePasskeyPrompt();
          return;
        }
        const s = err.response && err.response.status;
        if (s === 401) {
          this.passkeyError = "รหัสผ่านไม่ถูกต้อง";
        } else if (s === 423) {
          this.passkeyError = "บัญชีถูกล็อกชั่วคราว";
        } else if (s === 501) {
          this.passkeyError =
            "อุปกรณ์นี้ยังไม่รองรับ Passkey สำหรับผู้ใช้กลุ่มนี้";
        } else if (s === 400 && stage === "start") {
          this.passkeyError = "ต้องเปิดใช้งานรหัสยืนยันตัวตน (TOTP) ก่อน";
        } else {
          this.passkeyError = "ไม่สามารถเพิ่ม Passkey ได้ กรุณาลองใหม่";
        }
      } finally {
        this.isLoading = false;
      }
    },
    async revokePasskey(id) {
      this.isLoading = true;
      try {
        await this.revokePasskeyRequest(id);
        this.showDialogToast("success", "เพิกถอน Passkey แล้ว");
        await this.fetchStatus();
      } catch (err) {
        this.showDialogToast("danger", "ไม่สามารถเพิกถอน Passkey ได้");
      } finally {
        this.isLoading = false;
      }
    },
    // A 400 from DELETE /api/user/mfa/totp is either "invalid code" or the
    // passkey refusal; the server names the passkeys in the latter, and the
    // locally known list is the fallback tell.
    isPasskeyBlockingDisable(err) {
      const body = (err.response && err.response.data) || {};
      const message = String(body.errorMessage || body.message || "");
      if (/passkey/i.test(message)) {
        return true;
      }
      return !!(
        this.status &&
        this.status.passkeys &&
        this.status.passkeys.length
      );
    },

    // ---- helpers ----
    onlyDigits(value) {
      return String(value || "")
        .replace(/\D/g, "")
        .slice(0, 6);
    },
    copySecret() {
      this.copyText(this.setup.secretBase32);
    },
    copyText(text) {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard
          .writeText(text)
          .then(() => this.showDialogToast("success", "คัดลอกแล้ว"))
          .catch(() => this.fallbackCopy(text));
      } else {
        this.fallbackCopy(text);
      }
    },
    fallbackCopy(text) {
      try {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        const ok = document.execCommand("copy");
        document.body.removeChild(ta);
        if (ok) {
          this.showDialogToast("success", "คัดลอกแล้ว");
        } else {
          this.showDialogToast("warning", "กรุณาคัดลอกด้วยตนเอง");
        }
      } catch (err) {
        this.showDialogToast("warning", "กรุณาคัดลอกด้วยตนเอง");
      }
    },
    formatDate(value) {
      if (!value) {
        return "-";
      }
      const d = new Date(value);
      if (isNaN(d.getTime())) {
        return "-";
      }
      return d.toLocaleString("th-TH");
    },
    clearForms() {
      this.showSetupPassword = false;
      this.setupPassword = "";
      this.setupError = "";
      this.activateCode = "";
      this.activateError = "";
      this.setup = { otpauthUri: "", secretBase32: "", qrDataUrl: "" };
      this.recoveryCodes = [];
      this.prompt = null;
      this.promptPassword = "";
      this.promptCode = "";
      this.promptError = "";
      this.passkeyPrompt = false;
      this.passkeyPassword = "";
      this.passkeyNickname = "";
      this.passkeyError = "";
    },
    showDialogToast(variant, textDetail) {
      this.$refs.Dialog.showToast(variant, textDetail);
    },
  },
};
</script>

<style scoped>
.mfa-qr-img {
  width: 200px;
  height: 200px;
  max-width: 100%;
}
.mfa-secret {
  font-family: monospace;
  letter-spacing: 1px;
}
.recovery-codes {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.5rem;
}
.recovery-code {
  font-family: monospace;
  font-size: 1rem;
  padding: 0.4rem 0.6rem;
  background: #f5f5f5;
  border-radius: 4px;
  text-align: center;
}
.description.error {
  color: #dc3545;
  font-size: 0.85rem;
  margin-top: 0.25rem;
}
</style>
